import { z } from "zod";
import { defineStepConfig, VisibilityPredicateType } from "@erp/workflows";
import { entityOfType } from "../../../utils/system-entities";
import { IntegrationKey, SystemEntity } from "@erp/shared-types";

/**
 * Fetch Purchase Order from Famous Step
 *
 * Fetches purchase order(s) from Famous Software by PO number(s) and ingests them
 * into our database as InternalPurchaseOrder(s) with line items.
 *
 * This step:
 * - Connects to Famous Oracle database
 * - Retrieves PO data with line items
 * - Validates with FamousPOHeaderSchema
 * - Creates/updates InternalPurchaseOrder and line items
 * - Handles all edge cases (deleted lines, supplier lookup, etc.)
 *
 * Input: `poNumbers` (array) must be provided at runtime.
 *
 * Note: This is a backend-only step that requires Famous database access.
 */
export const fetchPurchaseOrderFromFamousStep = defineStepConfig({
  type: "fetch_purchase_order_from_famous",
  name: "Fetch PO from Famous",
  description: "Fetch and ingest purchase order(s) from Famous Software",
  category: "Integration",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],
  inputSchema: z.object({
    poNumbers: z
      .array(z.string().trim())
      .optional()
      .describe(
        "Array of Famous purchase order numbers to fetch. At runtime, poNumbers must be provided.",
      ),
  }),

  outputSchema: z.object({
    purchaseOrders: z
      .array(entityOfType(SystemEntity.INTERNAL_PURCHASE_ORDER))
      .describe(
        "Array of Internal Purchase Orders (created/updated) with line items",
      )
      .optional(),
  }),

  configSchema: z.object({
    cancelIfNotFound: z
      .boolean()
      .prefault(false)
      .describe("Cancel the workflow if any purchase order is not found"),
  }),

  emits: [] as const,
});
